import { useEffect, useState } from 'react';
import { useStore } from '../store/useStore.js';

function stripHtml(html: string): string {
  if (!html) return '';
  try {
    return new DOMParser().parseFromString(html, 'text/html').body.textContent ?? '';
  } catch {
    return html.replace(/<[^>]*>/g, '');
  }
}

export function NodePreviewTooltip() {
  const selectedId = useStore((s) => s.selectedId);
  const editPanelOpen = useStore((s) => s.editPanelOpen);
  const nodes = useStore((s) => s.nodes);
  const cam = useStore((s) => s.cam);
  const linkingFrom = useStore((s) => s.linkingFrom);
  const editingLabelId = useStore((s) => s.editingLabelId);
  const noteEditingId = useStore((s) => s.noteEditingId);
  const [visibleId, setVisibleId] = useState<string | null>(null);

  useEffect(() => {
    if (!selectedId || editPanelOpen || linkingFrom || editingLabelId || noteEditingId) {
      setVisibleId(null);
      return;
    }
    const node = nodes.find((n) => n.id === selectedId);
    if (!node?.content?.trim()) { setVisibleId(null); return; }

    const t = setTimeout(() => setVisibleId(selectedId), 150);
    return () => clearTimeout(t);
  }, [selectedId, editPanelOpen, linkingFrom, editingLabelId, noteEditingId, nodes]);

  if (!visibleId || visibleId !== selectedId) return null;
  const node = nodes.find((n) => n.id === visibleId);
  if (!node?.content?.trim()) return null;

  const text = stripHtml(node.content).slice(0, 220).trim();
  if (!text) return null;

  // Compute screen position: right of node.
  const canvasW = window.innerWidth;
  const canvasH = window.innerHeight - 54;
  const sx = (node.x + cam.x) * cam.zoom + canvasW / 2;
  const sy = (node.y + cam.y) * cam.zoom + canvasH / 2 + 54;
  const r = (node.size || 22) * cam.zoom;

  // Place to the right; if too close to edge, place to the left.
  const tooltipW = 240;
  const leftPos = sx + r + 14 + tooltipW > window.innerWidth - 12
    ? sx - r - 14 - tooltipW
    : sx + r + 14;

  return (
    <div
      style={{
        position: 'fixed',
        left: leftPos,
        top: Math.min(sy - 40, window.innerHeight - 160),
        width: tooltipW,
        background: 'rgba(2, 6, 20, 0.92)',
        backdropFilter: 'blur(20px)',
        border: `1px solid rgba(${hexToRgb(node.color)}, 0.35)`,
        borderRadius: 10,
        padding: '10px 13px',
        zIndex: 70,
        pointerEvents: 'none',
        boxShadow: `0 4px 24px rgba(0,0,0,0.6), 0 0 0 1px rgba(${hexToRgb(node.color)}, 0.08)`,
      }}
    >
      <div style={{
        fontSize: 11, fontWeight: 700, color: node.color,
        marginBottom: 5, letterSpacing: '0.04em',
        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
      }}>
        {node.label}
      </div>
      <div style={{
        fontSize: 11, color: 'var(--muted)', lineHeight: 1.55,
        display: '-webkit-box', WebkitLineClamp: 6,
        WebkitBoxOrient: 'vertical', overflow: 'hidden',
      }}>
        {text}{text.length >= 220 ? '…' : ''}
      </div>
    </div>
  );
}

function hexToRgb(hex: string): string {
  try {
    const r = parseInt(hex.slice(1, 3), 16);
    const g = parseInt(hex.slice(3, 5), 16);
    const b = parseInt(hex.slice(5, 7), 16);
    return `${r},${g},${b}`;
  } catch {
    return '0,245,255';
  }
}
